'use client';

import addToCartTrigger from '@coolblue-development/ga4-events/triggers/ComparisonTool/addToCartTrigger';
import { useAnalytics } from '@coolblue-development/next-analytics/client';
import { OutOfStockButton } from '@coolblue-development/next-patterns/client';
import { MandatoryPostalCodeCheckerModal } from '@coolblue-development/next-postal-code/client';
import { getPostalCode } from '@coolblue-development/next-postal-code/server';
import { AddToCartButton } from '@coolblue-development/next-shopping-cart/client';
import { useTranslation } from '@i18n/useTranslation';
import { addProductToShoppingCart } from '@utils/nextShoppingCartActions/addProductToShoppingCart';
import { useRouter } from 'next/navigation';
import { useRef, useState } from 'react';

import { AvailabilityState, type Product } from '../../typings/types';

type Props = {
  product: Product;
  index: number;
  totalIndices: number;
};

function ComparisonPageAddToCartButton({ product, index, totalIndices }: Props) {
  const [isLoading, setIsLoading] = useState(false);
  const [isMandatoryPostalCodeCheckerOpen, setIsMandatoryPostalCodeCheckerOpen] = useState(false);

  const router = useRouter();
  const { trackEvent } = useAnalytics();
  const { t } = useTranslation('webshop');

  function handleSubmit() {
    addProductToShoppingCart({
      productId: product.id,
      revalidate: typeof window === 'undefined'
    })
      .then(response => {
        trackEvent(
          addToCartTrigger({
            item_id: product.id,
            order_type: 'purchase',
            index,
            total_indices: totalIndices
          })
        );

        const redirectUrl = response?.data?.redirectUrl;

        setIsLoading(false);

        if (redirectUrl && typeof window !== 'undefined') {
          router.push(redirectUrl);
        }
      })
      .catch(() => {
        setIsLoading(false);
      });
  }

  const inStock = [AvailabilityState.OnStock, AvailabilityState.PreOrder].includes(
    product.availabilityState
  );

  const formRef = useRef<HTMLFormElement>(null);

  return (
    <form
      ref={formRef}
      onSubmit={async event => {
        event.preventDefault();
        setIsLoading(true);

        if (product.isMandatoryPostalCodeSplitTestInVariation) {
          if (!(await getPostalCode())) {
            setIsLoading(false);
            setIsMandatoryPostalCodeCheckerOpen(true);
            return;
          }
        }

        handleSubmit();
      }}
    >
      {inStock ? (
        <AddToCartButton
          loading={isLoading}
          ariaLabel={t('compare.addToCart.label')}
          disabled={product.shouldDisableAddToCart}
        />
      ) : (
        <OutOfStockButton
          variant='compact'
          ariaLabel={t('compare.activeMailer.subscribeButtonLabel')}
          href={product.urls.product}
        />
      )}

      <MandatoryPostalCodeCheckerModal
        productId={product.id}
        isOpen={isMandatoryPostalCodeCheckerOpen}
        setIsOpen={setIsMandatoryPostalCodeCheckerOpen}
        formRef={formRef}
      />
    </form>
  );
}

export default ComparisonPageAddToCartButton;
